// Per-role run settings — see docs/plans/2026-09-30-runners/05-web-en-rolinstellingen.md §2.1.
//
// `RoleRunSettingsSchema` is the single contract for what a run may do beyond
// the four built-in tools (web, sub-tasks, notebooks, Pi extensions, extra
// approval patterns) and how much (limits). It is stored as
// `role_versions.run_settings` (API), read by the worker to derive the guard
// and budget payload (02, 06), and rendered in the role editor (05's own UI
// work — not part of this scaffold).
//
// Contract rule, load-bearing for every future role version: parsing fills
// in whatever the caller left out, and every fallback is the *safe* one — a
// missing switch resolves to "off", a missing limit resolves to its default.
// An old role version (saved before a field existed) must never gain more
// rights than it explicitly has just because a key is absent.

import { z } from 'zod'

// ---------------------------------------------------------------------------
// Shared value schemas
// ---------------------------------------------------------------------------

/** A hostname, or a `*.example.com` wildcard — lowercase, at most 100 characters. */
export const RunSettingsDomainSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(
    /^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
    'Use a lowercase hostname, or "*.example.com"'
  )

// ---------------------------------------------------------------------------
// web.*
// ---------------------------------------------------------------------------

export const WebRunSettingsSchema = z
  .object({
    enabled: z.boolean().default(false),
    /** Empty = every public domain (subject to `blockedDomains`). */
    allowedDomains: z.array(RunSettingsDomainSchema).max(100).default([]),
    blockedDomains: z.array(RunSettingsDomainSchema).max(100).default([]),
    /** `null` = the workspace's first active search connection. */
    searchConnectionId: z.string().min(1).nullable().default(null),
  })
  .default({})

export type WebRunSettings = z.infer<typeof WebRunSettingsSchema>

// ---------------------------------------------------------------------------
// delegate.*
// ---------------------------------------------------------------------------

export const DelegateRunSettingsSchema = z
  .object({
    enabled: z.boolean().default(false),
    /** 1-3; one level deep is fixed (not configurable). */
    maxConcurrent: z.number().int().min(1).max(3).default(3),
    /** `null` = the agent's own model. */
    model: z.string().min(1).nullable().default(null),
  })
  .default({})

export type DelegateRunSettings = z.infer<typeof DelegateRunSettingsSchema>

// ---------------------------------------------------------------------------
// kb.* — knowledge base run-tools (docs/plans/2026-10-02-kennisbank-ontwerp.md §7.1)
// ---------------------------------------------------------------------------

/**
 * Deliberate exception to the "missing switch = off" rule above: the
 * knowledge base is on by default (ontwerp §7.1, "standaard aan"), so every
 * existing role reads and writes it without a role edit. Writes are still
 * bounded by folder policy (`conventions/` = proposal), 20 per run and a
 * revision per change, so a role can always be narrowed to read-only.
 */
export const KbRunSettingsSchema = z
  .object({
    /** `kb_list`/`kb_read`/`kb_search`/`kb_for_paths` + the prompt section. */
    enabled: z.boolean().default(true),
    /** `kb_write` (only when `enabled` is on as well). */
    write: z.boolean().default(true),
  })
  .default({})

export type KbRunSettings = z.infer<typeof KbRunSettingsSchema>

// ---------------------------------------------------------------------------
// tools.*
// ---------------------------------------------------------------------------

/**
 * Per-role switches for the four optional run tools that aren't core
 * (`ask_human`, `request_approval`, `post_comment`, `create_followup` —
 * `run-tools/catalog.ts`'s `DEFAULT_RUN_TOOLS` minus `get_task`/
 * `report_progress`/`finish`, which are core and have no switch: every role
 * always gets those three).
 *
 * Every key here defaults to TRUE — the opposite of `web`/`delegate` above,
 * and deliberately so. `web`/`delegate` gate a tool that doesn't exist for a
 * role until it opts in, so "off" is the safe default for them. These four
 * tools, by contrast, were already unconditionally granted to every role
 * before this field existed; defaulting any of them to `false` would widen
 * nothing in the schema sense, but it would silently *take away* a
 * capability from every already-stored role version the moment it's read
 * back (`parseRoleRunSettings` fills in whatever a stored row is missing).
 * The module doc's "a missing switch resolves to off" rule is about never
 * granting more than a role explicitly has; here "more than it explicitly
 * has" is TRUE, so TRUE is the backward-compatible default.
 */
export const RunToolsSettingsSchema = z
  .object({
    askHuman: z.boolean().default(true),
    requestApproval: z.boolean().default(true),
    postComment: z.boolean().default(true),
    createFollowup: z.boolean().default(true),
    /**
     * `create_subtasks` (docs/plans/2026-10-03-subtaken.md §5). Unlike the
     * four above it defaults to FALSE: it is new, no stored role had it,
     * and splitting a task into tens of others is a planner's job only.
     */
    createSubtasks: z.boolean().default(false),
  })
  .default({})

export type RunToolsSettings = z.infer<typeof RunToolsSettingsSchema>

// ---------------------------------------------------------------------------
// reports.* — per-task reports for humans (docs/plans/2026-10-02-rapporten-ontwerp.md §2.7)
// ---------------------------------------------------------------------------

/**
 * Same deliberate exception as `kb`: on by default ("standaard aan"). A
 * report is bounded per run (20 writes, 30 attachments) and is for humans
 * only — nothing in it reaches another system automatically. `read: false`
 * keeps an agent away from earlier decisions on purpose (no bias): no
 * `report_read`, no report line in the prompt, and the API refuses the
 * read for that run.
 */
export const ReportsRunSettingsSchema = z
  .object({
    /** `report_read` + the prompt line about the task's existing report. */
    read: z.boolean().default(true),
    /** `report_write` + `report_attach`. */
    write: z.boolean().default(true),
  })
  .default({})

export type ReportsRunSettings = z.infer<typeof ReportsRunSettingsSchema>

// ---------------------------------------------------------------------------
// limits.*
// ---------------------------------------------------------------------------

export const RunLimitsSchema = z
  .object({
    modelCalls: z.number().int().min(1).max(2000).default(100),
    /** New input + cache-creation + output tokens; cache reads don't count. */
    tokens: z.number().int().min(10_000).max(50_000_000).default(2_000_000),
    webSearches: z.number().int().min(0).max(200).default(20),
    webFetches: z.number().int().min(0).max(500).default(40),
    subtasks: z.number().int().min(0).max(50).default(5),
  })
  .default({})

export type RunLimits = z.infer<typeof RunLimitsSchema>

// ---------------------------------------------------------------------------
// onQuota.* — what a run does when its connection hits a usage limit
// (docs/plans/2026-10-02-checkpoints-en-overdracht.md §3.4, contract §3.5)
// ---------------------------------------------------------------------------

export const OnQuotaModeSchema = z.enum(['wait', 'handoff', 'handoff_then_wait'])
export type OnQuotaMode = z.infer<typeof OnQuotaModeSchema>

/**
 * `wait` (default, safe: same agent, same model, the real conversation):
 * pause until the limit resets. `handoff`: the first usable fallback agent
 * takes over, else the run is `blocked`. `handoff_then_wait`: hand off when a
 * fallback is usable, else wait. With no `fallbackAgentIds`, every mode
 * behaves as `wait`.
 */
export const OnQuotaSettingsSchema = z
  .object({
    mode: OnQuotaModeSchema.default('wait'),
    /** Agent ids in order of preference; each must be able to run this role. */
    fallbackAgentIds: z.array(z.string().uuid()).max(5).default([]),
    /** Handoffs per chain (automatic and manual together). */
    maxHandoffs: z.number().int().min(0).max(5).default(2),
  })
  .default({})

export type OnQuotaSettings = z.infer<typeof OnQuotaSettingsSchema>

// ---------------------------------------------------------------------------
// The full role run-settings contract
// ---------------------------------------------------------------------------

export const RoleRunSettingsSchema = z
  .object({
    web: WebRunSettingsSchema,
    delegate: DelegateRunSettingsSchema,
    /** Switches for the four optional (non-core) run tools — see `RunToolsSettingsSchema`'s own doc comment for why these default to TRUE. */
    tools: RunToolsSettingsSchema,
    kb: KbRunSettingsSchema,
    reports: ReportsRunSettingsSchema,
    /** Claude Code only (Pi has no NotebookEdit tool). */
    notebookEdit: z.boolean().default(false),
    /** Ids from the managed Pi extension catalog (03); unknown ids are the adapter's job to reject. */
    piExtensions: z.array(z.string().min(1)).default([]),
    /** Extra Bash approval patterns on top of the worker's standard list (02). */
    askPatterns: z.array(z.string().min(1)).default([]),
    /**
     * TEMPORARY, for the 01 A/B test only: which system prompt Claude Code
     * runs with. Defaults to 'native' so existing runs don't change before
     * the test decides. Removed from the schema once that decision lands.
     */
    claudePrompt: z.enum(['goblic', 'native']).default('native'),
    limits: RunLimitsSchema,
    /** Usage-limit policy (checkpoints contract §3.5). */
    onQuota: OnQuotaSettingsSchema,
    /**
     * Same-container runner restarts after a `transient`/`rate_limit`
     * failure, per run (design §3.3). 0 = fail (with a checkpoint) at once.
     * A `rate_limit` past this many retries is treated as `quota`.
     */
    maxTransientRetries: z.number().int().min(0).max(5).default(3),
  })
  .default({})

export type RoleRunSettings = z.infer<typeof RoleRunSettingsSchema>

/** The full object every field falls back to when nothing is supplied at all. */
export const DEFAULT_ROLE_RUN_SETTINGS: RoleRunSettings = RoleRunSettingsSchema.parse(undefined)

/**
 * Run settings for the built-in Planner role: the defaults plus the
 * `create_subtasks` tool, which the "Large work (subtasks)" workflow template
 * needs for its Split step (off for every other role).
 */
export const PLANNER_RUN_SETTINGS: RoleRunSettings = RoleRunSettingsSchema.parse({
  tools: { createSubtasks: true },
})

/**
 * Throwing parse — fills in every missing key with its safe default (see the
 * module doc comment). Use whenever a role version's `run_settings` column
 * is read back, so older rows transparently gain the new defaults instead of
 * failing validation.
 */
export function parseRoleRunSettings(input: unknown): RoleRunSettings {
  return RoleRunSettingsSchema.parse(input)
}

/** Non-throwing parse, for validating caller-supplied `run_settings` (e.g. a role update request). */
export function safeParseRoleRunSettings(input: unknown) {
  return RoleRunSettingsSchema.safeParse(input)
}

// ---------------------------------------------------------------------------
// Per-agent override (docs/plans/2026-10-02-rapporten-ontwerp.md §2.7)
// ---------------------------------------------------------------------------

/**
 * The only run settings a per-agent override may touch: on/off switches.
 * Limits, approval patterns (`askPatterns`), domain lists, models, Pi
 * extensions and the Claude prompt stay the role's — an override must never
 * be a way to loosen what the role's editor decided (an `askPatterns: []`
 * would otherwise wipe the role's approval patterns).
 */
export const RUN_SETTINGS_OVERRIDE_SWITCHES = {
  web: ['enabled'],
  delegate: ['enabled'],
  kb: ['enabled', 'write'],
  reports: ['read', 'write'],
  tools: ['askHuman', 'requestApproval', 'postComment', 'createFollowup', 'createSubtasks'],
} as const

export type RunSettingsOverrideGroup = keyof typeof RUN_SETTINGS_OVERRIDE_SWITCHES

/**
 * `agents.run_settings_override`: per-agent on/off switches that win over
 * the role's settings at dispatch (`effectiveRunSettings`). Every key is
 * optional; an absent key keeps the role's value. Strict, for validating
 * what callers save (agents API, Goblic-MCP, web): any other key is refused.
 */
export const RunSettingsOverrideSchema = z
  .object({
    web: z.object({ enabled: z.boolean() }).partial().strict().optional(),
    delegate: z.object({ enabled: z.boolean() }).partial().strict().optional(),
    kb: z.object({ enabled: z.boolean(), write: z.boolean() }).partial().strict().optional(),
    reports: z.object({ read: z.boolean(), write: z.boolean() }).partial().strict().optional(),
    tools: z
      .object({
        askHuman: z.boolean(),
        requestApproval: z.boolean(),
        postComment: z.boolean(),
        createFollowup: z.boolean(),
        createSubtasks: z.boolean(),
      })
      .partial()
      .strict()
      .optional(),
  })
  .strict()

export type RunSettingsOverride = z.infer<typeof RunSettingsOverrideSchema>

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export interface RunSettingsOverrideParse {
  override: RunSettingsOverride
  /** Keys that were dropped (unknown, or no longer overridable) — harmless, the role's value stays. */
  stripped: string[]
  /** Switches whose value wasn't a boolean — forced off (the safe side). */
  invalid: string[]
}

/**
 * Lenient read of a stored override (a row saved before the schema
 * narrowed, or edited by hand): unknown keys are stripped (the role's value
 * stays); a switch whose value isn't a boolean resolves to `false`, so a
 * broken override can only ever narrow the role, never widen it. An
 * override that isn't an object at all turns every overridable switch off.
 */
export function parseRunSettingsOverride(input: unknown): RunSettingsOverrideParse {
  const stripped: string[] = []
  const invalid: string[] = []
  const override: Record<string, Record<string, boolean>> = {}
  const groups = RUN_SETTINGS_OVERRIDE_SWITCHES as Record<string, readonly string[]>
  if (!isPlainObject(input)) {
    for (const [group, keys] of Object.entries(groups)) {
      override[group] = Object.fromEntries(keys.map((key) => [key, false]))
      for (const key of keys) invalid.push(`${group}.${key}`)
    }
    return { override: override as RunSettingsOverride, stripped, invalid }
  }
  for (const [group, value] of Object.entries(input)) {
    if (!Object.prototype.hasOwnProperty.call(groups, group)) {
      stripped.push(group)
      continue
    }
    const keys = groups[group]!
    if (value === undefined) continue
    if (!isPlainObject(value)) {
      override[group] = Object.fromEntries(keys.map((key) => [key, false]))
      for (const key of keys) invalid.push(`${group}.${key}`)
      continue
    }
    const block: Record<string, boolean> = {}
    for (const [key, setting] of Object.entries(value)) {
      if (!keys.includes(key)) {
        stripped.push(`${group}.${key}`)
        continue
      }
      if (setting === undefined) continue
      if (typeof setting === 'boolean') block[key] = setting
      else {
        block[key] = false
        invalid.push(`${group}.${key}`)
      }
    }
    if (Object.keys(block).length > 0) override[group] = block
  }
  return { override: override as RunSettingsOverride, stripped, invalid }
}

export interface EffectiveRunSettingsOptions {
  /** Called when a stored override had unknown keys or invalid values; defaults to `console.warn`. */
  onOverrideIssue?: (issue: { stripped: string[]; invalid: string[] }) => void
}

/**
 * A run's effective settings: the role version's `run_settings` (parsed with
 * the safe defaults) with the agent's `run_settings_override` switches set
 * over it — the agent wins, switch by switch. Use this everywhere a run's
 * settings are decided at dispatch; afterwards the run's own snapshot
 * (`runs.config_snapshot.runSettings`, `runSettingsFromSnapshot`) is the
 * source. A stored override is read leniently (`parseRunSettingsOverride`):
 * unknown keys are dropped, a non-boolean switch is off.
 */
export function effectiveRunSettings(roleRunSettings: unknown, agentOverride?: unknown, options: EffectiveRunSettingsOptions = {}): RoleRunSettings {
  const role = parseRoleRunSettings(roleRunSettings ?? undefined)
  if (agentOverride === null || agentOverride === undefined) return role
  const { override, stripped, invalid } = parseRunSettingsOverride(agentOverride)
  if (stripped.length > 0 || invalid.length > 0) {
    const report = options.onOverrideIssue ?? ((issue) => console.warn(`run_settings_override: ignored ${issue.stripped.join(', ') || 'nothing'}; forced off ${issue.invalid.join(', ') || 'nothing'}`))
    report({ stripped, invalid })
  }
  // An override may only *narrow* the role, never widen it (this module's
  // own doc comment on `RUN_SETTINGS_OVERRIDE_SWITCHES`): for every boolean
  // switch, the effective value is `role && override`, so a role switch
  // that's off stays off no matter what the agent's override says.
  const merged: RoleRunSettings = { ...role }
  for (const [group, block] of Object.entries(override) as [RunSettingsOverrideGroup, Record<string, boolean>][]) {
    const roleBlock = role[group] as Record<string, boolean>
    const narrowed = Object.fromEntries(Object.entries(block).map(([key, value]) => [key, Boolean(roleBlock[key]) && value]))
    merged[group] = { ...role[group], ...narrowed } as never
  }
  return RoleRunSettingsSchema.parse(merged)
}

/**
 * The effective run settings frozen on a run at dispatch
 * (`runs.config_snapshot.runSettings`), or `null` when the run has none
 * (dispatched before the snapshot existed) or it doesn't parse — the caller
 * then falls back to `effectiveRunSettings` on the live role + override.
 */
export function runSettingsFromSnapshot(configSnapshot: unknown): RoleRunSettings | null {
  if (!isPlainObject(configSnapshot) || !isPlainObject(configSnapshot.runSettings)) return null
  const parsed = RoleRunSettingsSchema.safeParse(configSnapshot.runSettings)
  return parsed.success ? parsed.data : null
}
